import { useMemo, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import useVillasPrice from './useVillasPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'

interface Props {
  packageView: App.OfferPackageOptionView
  offer: App.Offer
  rooms: Array<App.Occupants>
  priceTrackedRef?: React.RefObject<boolean>
}

function VillasPriceDetails(props: Props) {
  const { packageView, offer, rooms, priceTrackedRef } = props
  const intl = useIntl()

  const [price, value, discount] = useVillasPrice(packageView)

  const showIncludedGuestsExceeded = useMemo(
    () =>
      !packageView?.package.roomRate?.extraGuestSurcharges.length &&
      includedGuestsExceededItems(rooms || [], packageView?.package, offer),
    [packageView, rooms, offer],
  )

  const shouldTrackPrice = !!price?.hotel
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && offer?.id && priceTrackedRef && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: price.hotel.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, price?.hotel, offer?.id])

  return <div>
    <LuxPlusPriceGuard
      showMemberPrice={price.member > 0}
      fallbackPrice={<PriceRowPrice
        size="L"
        price={price.hotel}
        saleUnit="total"
      />}
    >
      <LuxPlusPriceStack
        size="L"
        price={price.hotel}
        memberPrice={price.member}
        saleUnit="total"
      />
    </LuxPlusPriceGuard>
    {showIncludedGuestsExceeded && (
      <AdditionalGuestsPopup
        description={intl.formatMessage({
          id: 'Fog9v500RHKGpXJNfkJ-',
          defaultMessage: '+ Extra guest surcharge',
          description: 'Label for additional guest surcharge in villa pricing',
        })}
        modalContent={packageView.package.roomPolicyDescription}
      />
    )}
    {!!value && <PriceRowValueDiscountWithCaption
      size="L"
      originalValue={value}
      discountPercentage={discount}
    />}
  </div>
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
    offer: state.offer,
    rooms: state.rooms,
  }
}, VillasPriceDetails)
